import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import { Button, Dialog, DialogActions, DialogContent, LinearProgress, Typography, Box } from '@mui/material';
import { AxiosError } from 'axios';
import { useState, useRef, useEffect, ReactNode } from 'react';
import { TitleText } from '../TitleText';

type OperationStatus = 'pending' | 'processing' | 'success' | 'error';

interface OperationState<T> {
  item: T;
  status: OperationStatus;
  error?: string;
}

interface BulkModalLabels {
  title: string;
  action: string;
  actionPastTense: string;
  actionPresent: string;
  confirmButton: string;
  cancelButton?: string;
  itemSingular: string;
  itemPlural: string;
  description?: string | ((count: number) => string);
}

interface BulkModalProperties<T extends { id: string }> {
  open: boolean;
  onClose: () => void;
  selectedItems: T[];
  onComplete?: () => void;
  performOperation: (item: T) => Promise<void>;
  labels: BulkModalLabels;
  buttonColor?: 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';
  dataTestId?: string;
  children?: ReactNode;
  actionsStart?: ReactNode;
  isConfirmDisabled?: boolean;
  autoCloseOnSuccess?: boolean;
}

export const BulkModal = <T extends { id: string }>({
  open,
  onClose,
  selectedItems,
  onComplete,
  performOperation,
  labels,
  buttonColor = 'error',
  dataTestId,
  children,
  actionsStart,
  isConfirmDisabled = false,
  autoCloseOnSuccess = true,
}: BulkModalProperties<T>) => {
  const { showToast } = useToast();
  const [operationStates, setOperationStates] = useState<OperationState<T>[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  const initializeOperationStates = () => {
    setOperationStates(selectedItems.map(item => ({ item, status: 'pending' })));
  };

  const finalizeOperation = (successCount: number, errorCount: number) => {
    setIsProcessing(false);

    if (successCount > 0) {
      showToast({
        message: `Successfully ${labels.actionPastTense.toLowerCase()} ${pluralize(successCount, labels.itemSingular, labels.itemPlural)}`,
        type: 'success',
      });
    }

    if (errorCount > 0) {
      showToast({
        message: `Failed to ${labels.action} ${pluralize(errorCount, labels.itemSingular, labels.itemPlural)}`,
        type: 'error',
      });
    }

    if (errorCount === 0 && autoCloseOnSuccess) {
      handleClose();
    }
    onComplete?.();
  };

  const handleStartOperation = async () => {
    if (abortControllerReference.current) {
      return;
    }
    setIsProcessing(true);
    initializeOperationStates();

    const abortController = new AbortController();
    abortControllerReference.current = abortController;

    let successCount = 0;
    let errorCount = 0;

    for (const item of selectedItems) {
      if (abortController.signal.aborted) {
        break;
      }

      setOperationStates(previous =>
        previous.map(state => (state.item.id === item.id ? { ...state, status: 'processing' } : state))
      );

      try {
        await performOperation(item);
        if (!abortController.signal.aborted) {
          setOperationStates(previous =>
            previous.map(state => (state.item.id === item.id ? { ...state, status: 'success' } : state))
          );
          successCount += 1;
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          const message =
            (error as AxiosError<{ message?: string }>).response?.data?.message ??
            `Failed to ${labels.action} ${labels.itemSingular}`;
          setOperationStates(previous =>
            previous.map(state => (state.item.id === item.id ? { ...state, status: 'error', error: message } : state))
          );
          errorCount += 1;
        }
      }
    }

    if (abortController.signal.aborted) {
      setIsProcessing(false);
      showToast({
        message: `${labels.title} cancelled. ${successCount} ${pluralize(successCount, labels.itemSingular, labels.itemPlural)} ${labels.actionPastTense} before cancellation.`,
        type: 'info',
      });
    } else {
      finalizeOperation(successCount, errorCount);
    }

    abortControllerReference.current = undefined;
  };

  const handleClose = () => {
    if (abortControllerReference.current) {
      abortControllerReference.current.abort();
    }
    onClose();
    setOperationStates([]);
  };

  useEffect(() => {
    return () => {
      if (abortControllerReference.current) {
        abortControllerReference.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (open) return;
    if (abortControllerReference.current) {
      abortControllerReference.current.abort();
      abortControllerReference.current = undefined;
    }
    setIsProcessing(false);
    setOperationStates([]);
  }, [open]);

  const itemCount = selectedItems.length;
  const completedCount = operationStates.filter(state => state.status === 'success' || state.status === 'error').length;
  const progress = operationStates.length > 0 ? (completedCount / operationStates.length) * 100 : 0;

  const isComplete = operationStates.length > 0 && !isProcessing;
  const cancelButtonLabel = isComplete ? 'Close' : labels.cancelButton || 'Cancel';

  const description =
    typeof labels.description === 'function'
      ? labels.description(itemCount)
      : labels.description ||
        `${pluralize(itemCount, labels.itemSingular, labels.itemPlural)} will be ${labels.actionPastTense}. This action cannot be undone.`;

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : handleClose}
      fullWidth
      data-testid={dataTestId}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>{labels.title}</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          {description}
        </Typography>
        {children}
        {(isProcessing || operationStates.length > 0) && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='body2' color='textSecondary'>
                {isProcessing
                  ? `${labels.actionPresent} ${completedCount} of ${operationStates.length}`
                  : `${labels.actionPastTense} ${completedCount} of ${operationStates.length}`}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress variant='determinate' value={progress} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        {actionsStart && <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>{actionsStart}</Box>}
        <Button
          onClick={handleClose}
          disabled={operationStates.length > 0 && operationStates.every(state => state.status === 'success')}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
        >
          {cancelButtonLabel}
        </Button>
        <Button
          onClick={() => {
            void handleStartOperation();
          }}
          color={buttonColor}
          variant='contained'
          disabled={isProcessing || operationStates.some(state => state.status === 'success') || isConfirmDisabled}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
        >
          {isProcessing ? `${labels.actionPresent}...` : labels.confirmButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
